import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import ChatFailureType from "@mapmycustomers/shared/enum/chat/ChatFailureType";
import ChatLocalMessageType from "@mapmycustomers/shared/enum/chat/ChatLocalMessageType";
import ChatMessageSource from "@mapmycustomers/shared/enum/chat/ChatMessageSource";
import {
  AssistantChatMessage,
  ChatContextEntityRef,
  ChatVoiceMeta,
} from "@mapmycustomers/shared/types/chat/ChatMessage";
import ChatMessage from "@mapmycustomers/shared/types/chat/ChatMessage";
import ChatSuggestion from "@mapmycustomers/shared/types/chat/ChatSuggestion";
import { FieldAgentApplyResultItem } from "@mapmycustomers/shared/types/chat/FieldAgentChat";

import { ChatProposalUiState } from "@app/util/chat/proposalRowStatus";

// The component asks to send; the saga mints the id and dispatches queueChatMessage, so the
// component never has to know about id shapes.
export const sendChatMessage = createAsyncAction(
  "@chat/conversation/sendChatMessage/request",
  "@chat/conversation/sendChatMessage/success",
  "@chat/conversation/sendChatMessage/failure"
)<
  { source?: ChatMessageSource; text: string },
  {
    conversationId: number;
    id: ChatMessage["id"];
    messages: AssistantChatMessage[];
    suggestions: ChatSuggestion[];
  },
  { failureType: ChatFailureType; id: ChatMessage["id"] }
>();

// Carries the timestamp as well as the id so one layer mints the whole message: the reducer
// stays a pure append instead of reading the clock part-way through a reduction.
export const queueChatMessage = createAction("@chat/conversation/queueChatMessage")<{
  contextEntity?: ChatContextEntityRef;
  createdAt: string;
  id: ChatMessage["id"];
  text: string;
  // Present iff the rep dictated rather than typed. Rides this same action into the message so
  // the tag persists and survives a retry exactly as the text it describes does.
  voice?: ChatVoiceMeta;
}>();

export const retryChatMessage = createAction("@chat/conversation/retryChatMessage")<{
  id: ChatMessage["id"];
}>();

// Deliberately does NOT carry the optimistic write itself — the saga dispatches
// applyChatMessageFeedback for that, below, once it has captured the true previous vote.
// If the reducer applied the optimistic value straight off this action, a failed write's
// revert would be reading state through the very watcher that action just woke: Redux runs
// reducers before notifying saga middleware, so the "previous" vote the saga read back would
// already be the new one, not the one it needs to restore.
export const setChatMessageFeedback = createAsyncAction(
  "@chat/conversation/setChatMessageFeedback/request",
  "@chat/conversation/setChatMessageFeedback/success",
  "@chat/conversation/setChatMessageFeedback/failure"
)<
  { feedback: boolean | null; id: ChatMessage["id"] },
  { id: ChatMessage["id"] },
  { feedback: boolean | null; id: ChatMessage["id"] }
>();

// The actual optimistic write, dispatched by the saga after it has already captured the
// prior vote in a local variable — so this action running through the reducer can never
// race the read that needs the value it's about to overwrite.
export const applyChatMessageFeedback = createAction(
  "@chat/conversation/applyChatMessageFeedback"
)<{ feedback: boolean | null; id: ChatMessage["id"] }>();

export const restoreChatConversation = createAction("@chat/conversation/restoreChatConversation")<{
  conversationId?: number;
  messages: ChatMessage[];
  proposalUi?: Record<number, ChatProposalUiState>;
}>();

export const clearChatConversation = createAction("@chat/conversation/clearChatConversation")();

// ENG-131 is the first producer of a local message. The renderer and the type already existed
// with nothing dispatching into them.
export const appendLocalChatMessage = createAction("@chat/conversation/appendLocalChatMessage")<{
  createdAt: string;
  id: ChatMessage["id"];
  localType: ChatLocalMessageType;
  text: string;
}>();

export const toggleProposalChecked = createAction("@chat/conversation/toggleProposalChecked")<{
  checked: boolean;
  proposalId: number;
}>();

export const saveProposalDraft = createAction("@chat/conversation/saveProposalDraft")<{
  draft: Record<string, unknown>;
  proposalId: number;
}>();

export const discardProposalDraft = createAction("@chat/conversation/discardProposalDraft")<{
  proposalId: number;
}>();

export const applyProposals = createAsyncAction(
  "@chat/conversation/applyProposals/request",
  "@chat/conversation/applyProposals/success",
  "@chat/conversation/applyProposals/failure"
)<
  { messageId: ChatMessage["id"] },
  { messageId: ChatMessage["id"]; results: FieldAgentApplyResultItem[] },
  { messageId: ChatMessage["id"] }
>();

// Plain request action; outcomes go through applyProposals.success/.failure. A second
// triple would duplicate outcome folding and allow Cancel and Confirm to run in parallel.
export const cancelProposals = createAction("@chat/conversation/cancelProposals")<{
  messageId: ChatMessage["id"];
}>();

export type ChatConversationActions = ActionType<
  | typeof appendLocalChatMessage
  | typeof applyChatMessageFeedback
  | typeof applyProposals
  | typeof cancelProposals
  | typeof clearChatConversation
  | typeof discardProposalDraft
  | typeof queueChatMessage
  | typeof restoreChatConversation
  | typeof retryChatMessage
  | typeof saveProposalDraft
  | typeof sendChatMessage
  | typeof setChatMessageFeedback
  | typeof toggleProposalChecked
>;
